import { ReactElement, useEffect, useState } from 'react';
import { formatNumberWithFractionDigits } from '~/helpers/thousandFormatNumber';
import { useWebSocketLazy, WSConn } from '~/helpers/wsConnection';

interface AmbientTemperatureValueProps {
  damId: string;
  /** Sensor que mede o ambiente: o do próprio DAM ou o DUT de referência. */
  source: { selfReference: boolean; dutId: string | null };
}

interface TemperatureMessage {
  type: string;
  data?: { dev_id?: string; Temperature?: number | null; status?: string };
}

/**
 * Temperatura atual do ambiente, pelo tempo real, como a tela do modo atual mostra. Montar de novo
 * (com outra `key`) ao trocar de sensor, pois a assinatura do tempo real é feita uma vez.
 */
export function AmbientTemperatureValue({ damId, source }: Readonly<AmbientTemperatureValueProps>): ReactElement {
  const [temperature, setTemperature] = useState<number | null>(null);
  const wsl = useWebSocketLazy();
  const { selfReference, dutId } = source;

  useEffect(() => {
    if (!selfReference && !dutId) return;
    wsl.start(
      (conn: WSConn) => {
        if (selfReference) conn.send({ type: 'subscribeStatus', data: { dam_id: damId } });
        else conn.send({ type: 'dutSubscribeRealTime', data: { DUT_ID: dutId } });
      },
      (message: TemperatureMessage) => {
        const isDut = message.type === 'dutTelemetry' && !selfReference && message.data?.dev_id === dutId;
        const isDam = message.type === 'damStatus' && selfReference && message.data?.dev_id === damId && !!message.data?.status;
        if (isDut || isDam) setTemperature(message.data?.Temperature ?? null);
      },
      (conn: WSConn) => {
        conn.send({ type: selfReference ? 'unsubscribeStatus' : 'dutUnsubscribeRealTime' });
      },
    );
  }, [damId, selfReference, dutId, wsl]);

  return <>{temperature == null ? '-' : `${formatNumberWithFractionDigits(temperature)} °C`}</>;
}
